import { Injectable, NgZone } from '@angular/core';
import { environment } from 'src/environments/environment';
import { APP_VERSION } from 'src/environments/version';
import { AuthenticationService } from '../services/authentication.service';
import { googleAuthFailure } from './basemap';

// Google Maps failures, reported to the API's log (conductor-server #283). Arrangers say address search "doesn't always
// load" but never say when, and a failure that never reaches Google can't show in Google's metrics. So the client
// reports what it sees: the script not arriving, the key refused, a field set up before Maps was ready, and the user's
// own symptom (typed, no suggestions on screen) with what was wrong and Google's answer for the same text.
//
// Never sends what was typed, only its length. One report per key per page load, at most SESSION_CAP per tab.
// Sent with fetch, not HttpClient, so a failed report can't reach the 401 interceptor or show the user anything.

export type MapsEventKind =
  | 'maps.script_not_loaded'
  | 'maps.auth_failure'
  | 'maps.autocomplete_init_before_ready'
  | 'maps.autocomplete_init_failed'
  | 'maps.autocomplete_no_suggestions'
  | 'maps.autocomplete_place_without_details';

type Detail = Record<string, string | number | boolean | null>;

const SCRIPT_GRACE_MS = 30000;
const SUGGESTION_WAIT_MS = 2500;
const PROBE_TIMEOUT_MS = 5000;
const SESSION_CAP = 25;
const SESSION_KEY = 'mapsDiagnosticsSent';

export function placesReady(): boolean {
  return typeof google !== 'undefined' && !!google?.maps?.places?.Autocomplete;
}

const claimed = new WeakSet<Element>();

/**
 * The suggestion list Google made for this input. The legacy widget creates it on the first keystroke, not with the
 * Autocomplete, and doesn't link it to the input, so it's the unclaimed .pac-container placed at the input's
 * bottom-left (Google sets left/top in page pixels each time it shows the list).
 */
export function findPacContainer(at: { left: number; top: number } | null, known: HTMLElement | null): HTMLElement | null {
  if (known?.isConnected) return known;
  if (!at) return null;
  const { left, top } = at;
  let best: HTMLElement | null = null;
  let bestDistance = 40;
  for (const el of Array.from(document.querySelectorAll<HTMLElement>('.pac-container'))) {
    if (claimed.has(el)) continue;
    const d = Math.abs(parseFloat(el.style.left) - left) + Math.abs(parseFloat(el.style.top) - top);
    if (d < bestDistance) {
      best = el;
      bestDistance = d;
    }
  }
  if (best) claimed.add(best);
  return best;
}

export interface AutocompleteWatch {
  stop(): void;
  /** The field's suggestion list, once found: for removing it with the field. */
  container(): HTMLElement | null;
}

@Injectable({ providedIn: 'root' })
export class MapsDiagnosticsService {
  private sent = new Set<string>();
  private queue: { kind: MapsEventKind; detail: Detail }[] = [];
  private flushTimer: any = null;
  private started = false;

  constructor(private zone: NgZone, private authenticationService: AuthenticationService) {}

  /** Once, from AppComponent: watch the script tag in index.html and Google's key check. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.zone.runOutsideAngular(() => {
      googleAuthFailure().then(() => this.report('maps.auth_failure', {}, 'auth-failure'));
      setTimeout(() => {
        if (!placesReady()) this.report('maps.script_not_loaded', { graceMs: SCRIPT_GRACE_MS }, 'script-not-loaded');
      }, SCRIPT_GRACE_MS);
    });
  }

  /** Resolves true once google.maps.places is usable, false after timeoutMs. */
  whenPlacesReady(timeoutMs: number): Promise<boolean> {
    if (placesReady()) return Promise.resolve(true);
    return new Promise((resolve) => {
      this.zone.runOutsideAngular(() => {
        const started = Date.now();
        const t = setInterval(() => {
          if (placesReady()) {
            clearInterval(t);
            this.zone.run(() => resolve(true));
          } else if (Date.now() - started > timeoutMs) {
            clearInterval(t);
            this.zone.run(() => resolve(false));
          }
        }, 100);
      });
    });
  }

  /**
   * Watches one Autocomplete input for the user's symptom: 3+ characters typed, still focused, and SUGGESTION_WAIT_MS
   * later no suggestion list on screen. Reports once (per onceKey) with what was wrong and what Google says for the same
   * text.
   */
  watchAutocomplete(
    input: HTMLInputElement,
    opts: { onceKey: string; field: string; componentRestrictions?: () => google.maps.places.ComponentRestrictions | undefined }
  ): AutocompleteWatch {
    let timer: any = null;
    let pac: HTMLElement | null = null;
    // Where the list goes (the input's bottom-left in page pixels), kept from the last keystroke: Angular can take the
    // input out of the page before the field's destroy hook asks for its list.
    let at: { left: number; top: number } | null = null;
    const place = () => {
      if (!input.isConnected) return;
      const r = input.getBoundingClientRect();
      at = { left: r.left + window.scrollX, top: r.bottom + window.scrollY };
    };
    const onInput = () => {
      clearTimeout(timer);
      place();
      if (input.value.trim().length < 3) return;
      timer = setTimeout(() => {
        place();
        pac = findPacContainer(at, pac);
        if (!this.sent.has(opts.onceKey)) this.diagnose(input, pac, opts);
      }, SUGGESTION_WAIT_MS);
    };
    this.zone.runOutsideAngular(() => input.addEventListener('input', onInput));
    return {
      stop: () => {
        clearTimeout(timer);
        input.removeEventListener('input', onInput);
      },
      container: () => {
        place();
        return (pac = findPacContainer(at, pac));
      },
    };
  }

  report(kind: MapsEventKind, detail: Detail, onceKey: string): void {
    if (this.sent.has(onceKey)) return;
    this.sent.add(onceKey);
    if (!this.takeSessionSlot()) return;
    this.queue.push({ kind, detail: { ...this.context(), ...detail } });
    if (!this.flushTimer) this.zone.runOutsideAngular(() => (this.flushTimer = setTimeout(() => this.flush(), 2000)));
  }

  private diagnose(
    input: HTMLInputElement,
    pac: HTMLElement | null,
    opts: { onceKey: string; field: string; componentRestrictions?: () => google.maps.places.ComponentRestrictions | undefined }
  ): void {
    const text = input.value.trim();
    if (this.sent.has(opts.onceKey) || text.length < 3 || document.activeElement !== input) return;

    const items = pac?.querySelectorAll('.pac-item').length ?? 0;
    const rect = pac?.getBoundingClientRect();
    const style = pac ? getComputedStyle(pac) : null;
    const shown = !!pac && !!rect && style?.display !== 'none' && style?.visibility !== 'hidden' && rect.height > 0;
    let covering: string | null = null;

    let outcome: string;
    if (!input.isConnected) outcome = 'input_detached';
    else if (!pac) outcome = 'no_container';
    else if (!pac.isConnected) outcome = 'container_detached';
    else if (items === 0) outcome = 'empty';
    else if (!shown) outcome = 'hidden';
    else if (rect!.bottom < 0 || rect!.top > window.innerHeight) outcome = 'offscreen';
    else {
      const first = pac.querySelector('.pac-item')!.getBoundingClientRect();
      const hit = document.elementFromPoint(first.left + first.width / 2, first.top + first.height / 2);
      if (hit && !pac.contains(hit)) {
        outcome = 'covered';
        covering = describe(hit);
      } else return; // Suggestions are on screen: nothing wrong.
    }

    const detail: Detail = {
      field: opts.field,
      outcome,
      textLength: text.length,
      items,
      containerDisplay: style?.display ?? null,
      containerZIndex: style?.zIndex ?? null,
      covering,
    };
    this.probe(text, opts.componentRestrictions?.()).then((r) =>
      this.report('maps.autocomplete_no_suggestions', { ...detail, ...r }, opts.onceKey)
    );
  }

  /** Asks Google for the same text directly, so the report says whether Google answered, refused or found nothing. */
  private probe(text: string, componentRestrictions?: google.maps.places.ComponentRestrictions): Promise<Detail> {
    if (!placesReady()) return Promise.resolve({ googleStatus: 'places_not_loaded', predictions: null });
    return new Promise((resolve) => {
      const t = setTimeout(() => resolve({ googleStatus: 'no_answer', predictions: null }), PROBE_TIMEOUT_MS);
      try {
        new google.maps.places.AutocompleteService().getPlacePredictions({ input: text, componentRestrictions }, (preds, status) => {
          clearTimeout(t);
          resolve({ googleStatus: String(status), predictions: preds?.length ?? 0 });
        });
      } catch (e: any) {
        clearTimeout(t);
        resolve({ googleStatus: 'probe_threw', predictions: null, error: String(e?.message ?? e) });
      }
    });
  }

  private context(): Detail {
    const w = window as any;
    const conn = (navigator as any).connection;
    return {
      page: location.hash.split('?')[0],
      msSinceLoad: Math.round(performance.now()),
      mapsLoadedAtMs: w.__gmLoadedAt ?? null,
      scriptErrorAtMs: w.__gmScriptError ?? null,
      authFailed: !!w.__gmAuthFailed,
      googleDefined: typeof google !== 'undefined',
      placesDefined: placesReady(),
      online: navigator.onLine,
      visibility: document.visibilityState,
      connection: conn?.effectiveType ?? null,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      appVersion: APP_VERSION,
    };
  }

  private takeSessionSlot(): boolean {
    try {
      const n = +(sessionStorage.getItem(SESSION_KEY) ?? 0);
      if (n >= SESSION_CAP) return false;
      sessionStorage.setItem(SESSION_KEY, String(n + 1));
    } catch {
      // No sessionStorage: the per-page-load keys still bound it.
    }
    return true;
  }

  /** Posts the queue once signed in; until then it waits (a report from the login screen goes when they sign in). */
  private flush(): void {
    this.flushTimer = null;
    const token = this.authenticationService.currentTokenValue;
    if (!this.queue.length) return;
    if (!token) {
      this.flushTimer = setTimeout(() => this.flush(), 30000);
      return;
    }
    const events = this.queue.splice(0, 20);
    fetch(environment.api.host + environment.api.paths.api.clientEvents, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ events }),
      keepalive: true,
    }).catch(() => {
      // Offline or the API is down: put them back for one more try.
      this.queue.unshift(...events);
      this.flushTimer = setTimeout(() => this.flush(), 60000);
    });
    if (this.queue.length) this.flushTimer = setTimeout(() => this.flush(), 2000);
  }
}

function describe(el: Element): string {
  const cls = typeof el.className === 'string' ? el.className.trim().split(/\s+/).slice(0, 3).join('.') : '';
  return (el.tagName.toLowerCase() + (cls ? '.' + cls : '')).slice(0, 120);
}
